import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  CreditCard as CardIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  ArrowForward as ArrowIcon,
  Receipt as ReceiptIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';
import { API_BASE_URL, getApiBaseUrlWithFallback } from '../config/environment';

export default function InterclubBilling({ interclubId }) {
  const { getIdToken } = useFirebaseAuth();
  
  const [interclub, setInterclub] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [stripeAccount, setStripeAccount] = useState(null);
  const [stats, setStats] = useState({ memberCount: 0, clubCount: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const loadInterclubData = useCallback(async () => {
    if (!interclubId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const token = await getIdToken();
      const baseUrl = await getApiBaseUrlWithFallback();
      
      // Load interclub details
      const interclubResponse = await fetch(`${baseUrl}/interclubs/${interclubId}`);
      if (interclubResponse.ok) {
        const data = await interclubResponse.json();
        setInterclub(data);
        
        // Check Stripe account status if stripeAccountId exists
        if (data.stripeAccountId) {
          try {
            const accountResponse = await fetch(
              `${API_BASE_URL}/stripe/account/${data.stripeAccountId}/status`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            if (accountResponse.ok) {
              const accountData = await accountResponse.json();
              setStripeAccount(accountData);
            }
          } catch (err) {
            console.error('Error loading Stripe account:', err);
          }
        }
      }
      
      // Load subscription status if applicable
      try {
        const subscriptionResponse = await fetch(
          `${API_BASE_URL}/stripe/subscription/interclub/${interclubId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (subscriptionResponse.ok) {
          const subData = await subscriptionResponse.json();
          setSubscription(subData);
        }
      } catch (err) {
        console.error('Error loading subscription:', err);
      }

      // Load stats from dashboard endpoint
      try {
        const REGISTRATION_BASE_URL = baseUrl.replace('/api', '/api/registration');
        const dashboardResponse = await fetch(
          `${REGISTRATION_BASE_URL}/interclub/${interclubId}/dashboard`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (dashboardResponse.ok) {
          const dashboardData = await dashboardResponse.json();
          if (dashboardData.dashboard) {
            setStats({
              memberCount: dashboardData.dashboard.users?.total || 0,
              clubCount: dashboardData.dashboard.clubs?.active || 0,
            });
          }
        }
      } catch (err) {
        console.error('Error loading stats:', err);
      }
      
    } catch (err) {
      console.error('Error loading billing data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [interclubId, getIdToken]);

  useEffect(() => {
    loadInterclubData();
  }, [loadInterclubData]);

  const handleCreateStripeAccount = async () => {
    setActionLoading(true);
    try {
      const token = await getIdToken();
      
      const response = await fetch(`${API_BASE_URL}/stripe/accounts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ interclubId }),
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create Stripe account');
      }
      
      const data = await response.json();
      
      // Redirect to Stripe onboarding
      if (data.onboardingUrl) {
        window.location.href = data.onboardingUrl;
      } else {
        await loadInterclubData();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleStripeOnboarding = async () => {
    if (!interclub?.stripeAccountId) return;
    
    setActionLoading(true);
    try {
      const token = await getIdToken();
      
      const response = await fetch(
        `${API_BASE_URL}/stripe/accounts/${interclub.stripeAccountId}/onboarding-link`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to get onboarding link');
      }
      
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleStripeDashboard = async () => {
    if (!interclub?.stripeAccountId) return;
    
    setActionLoading(true);
    try {
      const token = await getIdToken();
      
      const response = await fetch(
        `${API_BASE_URL}/stripe/accounts/${interclub.stripeAccountId}/dashboard-link`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to get dashboard link');
      }
      
      const data = await response.json();
      if (data.url) {
        window.open(data.url, '_blank');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSubscribe = async () => {
    setActionLoading(true);
    try {
      const token = await getIdToken();
      
      const response = await fetch(`${API_BASE_URL}/subscriptions/checkout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ interclubId }),
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create checkout session');
      }
      
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3 }}>
          💳 Stripe & Billing
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Stripe Connect Section */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CardIcon color="primary" />
              <Typography variant="h6">Payment Processing</Typography>
            </Box>
            
            {!interclub?.stripeAccountId ? (
              <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Set up Stripe Connect to accept payments for competition entries and season registrations.
                </Alert>
                <Button
                  variant="contained"
                  onClick={handleCreateStripeAccount}
                  disabled={actionLoading}
                  startIcon={<CardIcon />}
                >
                  {actionLoading ? 'Setting up...' : 'Set Up Stripe Connect'}
                </Button>
              </Box>
            ) : (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  {stripeAccount?.chargesEnabled ? (
                    <Chip icon={<CheckIcon />} label="Payments Enabled" color="success" />
                  ) : (
                    <Chip icon={<WarningIcon />} label="Setup Incomplete" color="warning" />
                  )}
                  {stripeAccount?.payoutsEnabled ? (
                    <Chip icon={<CheckIcon />} label="Payouts Enabled" color="success" />
                  ) : (
                    <Chip icon={<WarningIcon />} label="Payouts Pending" color="warning" />
                  )}
                </Box>
                
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {!stripeAccount?.chargesEnabled && (
                    <Button
                      variant="contained"
                      onClick={handleStripeOnboarding}
                      disabled={actionLoading}
                      startIcon={<ArrowIcon />}
                    >
                      Complete Setup
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    onClick={handleStripeDashboard}
                    disabled={actionLoading}
                    startIcon={<SettingsIcon />}
                  >
                    Stripe Dashboard
                  </Button>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Subscription Section */}
        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <ReceiptIcon color="primary" />
              <Typography variant="h6">Subscription</Typography>
            </Box>
            
            {subscription ? (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Chip
                    label={subscription.status === 'active' ? 'Active' : subscription.status}
                    color={subscription.status === 'active' ? 'success' : 'warning'}
                  />
                  {subscription.planName && (
                    <Typography variant="body2" color="text.secondary">
                      {subscription.planName}
                    </Typography>
                  )}
                </Box>
                
                <List dense>
                  {subscription.currentPeriodEnd && (
                    <ListItem>
                      <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                      <ListItemText 
                        primary="Billing Period"
                        secondary={`Renews on ${new Date(subscription.currentPeriodEnd).toLocaleDateString()}`}
                      />
                    </ListItem>
                  )}
                </List>
                
                <Button
                  variant="outlined"
                  href="/subscription/manage"
                  startIcon={<SettingsIcon />}
                >
                  Manage Subscription
                </Button>
              </Box>
            ) : (
              <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Your interclub doesn't have an active subscription. Subscribe to unlock all platform features.
                </Alert>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubscribe}
                  disabled={actionLoading}
                  startIcon={<ReceiptIcon />}
                >
                  {actionLoading ? 'Setting up...' : 'Subscribe Now - $5/month'}
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      </Paper>

      {/* Quick Stats */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Quick Stats</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary.main">
                {stats.memberCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Users
              </Typography>
            </CardContent>
          </Card>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main">
                {stats.clubCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Clubs
              </Typography>
            </CardContent>
          </Card>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="info.main">
                {stripeAccount?.chargesEnabled ? 'Active' : 'Pending'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Payment Status
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Paper>
    </Box>
  );
}
